import {
  DRAW_PROPS_ID,
  OrgChartElement,
  OrgChartOrientation,
  OrgChartViewType,
  PHOTO_ID,
} from "shared/datamodel/schemas/org-chart";
import { SeparationLayers, SeparationSiblings, TextFieldHeight } from "./shared/consts";
import { produceWithPatches } from "immer";
import consts from "shared/consts";
import { customAlphabet } from "nanoid";
import { any, pick, keys, fromPairs, map, pipe, reject, indexBy } from "rambda";
import { LayoutProps } from "./shared/tree-layout-utils";
import type { HierarchyPointNode, HierarchyNode } from "d3-hierarchy";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { ReadTransaction } from "@workcanvas/reflect";
import { canvasElementPrefix } from "shared/util/utils";
import { MindmapOrgChartNodeElement } from "shared/datamodel/schemas/mindmap-org-chart";
import { NodeData } from "./standalone/org-chart-skeleton";
import { buildMetadataEntry, fileExt } from "frontend/utils/file-utils";
import { PlanFeatureName, FileMetadataType, PLAN_FEATURES_DEFAULT_VALUES } from "shared/datamodel/schemas";
import { OrgChartItem } from "frontend/hooks/use-board-integrations.types";
import { LiveIntegrationColumn } from "frontend/canvas-designer-new/elements-toolbar/widgets/live-integration/utils";
import {
  NODE_DEFAULT_FIELDS_COUNT,
  NODE_ADDITIONAL_HEIGHT,
  TEXT_FIELD_HEIGHT,
} from "shared/datamodel/mindmap-org-chart";
import { ChartsFullDataLoadedState } from "shared/integrations/integration";
import { useBoardValue } from "frontend/state/board-state";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import { ITraits, Trait } from "frontend/canvas-designer-new/elements-toolbar/elements-toolbar-types";
import { Sizes, SizesConfig } from "frontend/data/shapes/sizes";
import { NodeDrawProps } from "./shared/types";
import { parseColor } from "utils/color-utils";

type MinimalTree = Pick<OrgChartElement, "data" | "layout">;
type LayoutData = Pick<OrgChartElement, "layout">["layout"];
export type Relationship = "parent" | "child" | "sibling-after" | "sibling-before";

export enum SourceNodeType {
  Person = "Person name",
  Department = "Department",
}

export interface NodeLayout {
  id: string;
  collapsed: boolean;
  childrenIds: string[];
}

interface RootData {
  rootLayout: NodeLayout;
  rootId: string;
  rootType: SourceNodeType;
}

const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
export const createNodeId = customAlphabet(alphabet, 10);

export const getItemView = (element: OrgChartElement | MindmapOrgChartNodeElement) =>
  element.type == "mindmapOrgChart" ? OrgChartViewType.NoProfilePicture : element.viewType ?? OrgChartViewType.Default;

export function transformerAnchorId(id?: string) {
  if (!id) return "anchor-transformer-for";
  return `anchor-transformer-for/${id}`;
}

export interface SmartNodeId {
  get fullElementId(): string;
  get rootId(): string;
  get nodeId(): string;
  isRoot: boolean;
}

export interface DragState {
  // mouse position on drag state (mouseEvent.screenX/Y)
  x0: number;
  y0: number;
  nodeIdUnderMouse: SmartNodeId;
  roots: SmartNodeId[];
  draggedNodes: string[];
  drop_targets: any[];
  stage: any;
  // current mouse x,y
  x: number;
  y: number;
  //
  dx: number;
  dy: number;
}

//-----------------------------------------------------------------------------
//#region Node id parsing and building
const nodeIdRegex = new RegExp(`${consts.CANVAS_ELEMENTS.ORG_CHART_NODE}-([^-]*)-([^-]*)`);
const rootIdRegex = new RegExp(`${consts.CANVAS_ELEMENTS.ORG_CHART}-([^-]*)`);

export function makeNodeId(rootId: string, nodeId?: string) {
  return nodeId
    ? `${consts.CANVAS_ELEMENTS.ORG_CHART_NODE}-${rootId}-${nodeId}`
    : `${consts.CANVAS_ELEMENTS.ORG_CHART}-${rootId}`;
}

export function isIdOfRoot(nodeId: string) {
  return nodeId.startsWith(consts.CANVAS_ELEMENTS.ORG_CHART);
}

const InvalidNodeId: SmartNodeId = {
  fullElementId: "",
  rootId: "",
  nodeId: "",
  isRoot: false,
};

export function parseNodeId(nodeId: string): SmartNodeId {
  const match = nodeIdRegex.exec(nodeId);
  if (match) {
    return {
      get fullElementId() {
        return consts.CANVAS_ELEMENTS.ORG_CHART + "-" + match[1];
      },
      get rootId() {
        return match[1];
      },
      get nodeId() {
        return match[2];
      },
      isRoot: false,
    };
  }

  const match2 = rootIdRegex.exec(nodeId);
  if (match2) {
    return {
      get fullElementId() {
        return nodeId;
      },
      get rootId() {
        return match2[1];
      },
      get nodeId() {
        return "root";
      },
      isRoot: true,
    };
  }
  return InvalidNodeId;
}
//#endregion

export const createNewIds = pipe(
  keys,
  map((id) => [id, createNodeId()] as [string, string]),
  fromPairs
);

// We've seen some cases where the layout has invalid childrenIds,
// this function cleans them up.
// Turns out, keeping childrenIds as an array was a bad-idea:
// 1. duplicates can popup (simultaneous drag-and-drop)
// 2. non-existing ids popup (no idea how)
//  -- it's better to keep a single parentId in the data source, and rebuild
//  -- the tree from that.
export function cleanupElement(element: OrgChartElement): OrgChartElement {
  const layout = element.layout;
  let needCleanup = false;
  for (const key in layout) {
    if (layout[key].childrenIds) {
      for (const id of layout[key].childrenIds) {
        if (!(id in layout)) {
          needCleanup = true;
        }
      }
    }
  }
  if (needCleanup) {
    const newLayout: OrgChartElement["layout"] = {};
    for (const key in layout) {
      newLayout[key] = { ...layout[key] };
      if (newLayout[key].childrenIds?.length) {
        newLayout[key].childrenIds = newLayout[key].childrenIds.filter((id) => id in layout);
      }
    }
    return { ...element, layout: newLayout };
  }
  return element;
}

//=============================================================================
//=============================================================================

type NodeID = string | number;
interface TreeLayout {
  getChildrenIds(id: NodeID): Iterable<NodeID>;
}

export function calcDescCount(layout: TreeLayout, rootId: NodeID) {
  let total: Record<NodeID, number> = {};
  function calc(id: NodeID) {
    total[id] ??= 0;
    for (const childId of layout.getChildrenIds(id)) {
      total[id] += 1 + calc(childId);
    }
    return total[id];
  }
  calc(rootId);
  return total;
}

export function makeTreeLayoutFromOrgChartData(layout: OrgChartElement["layout"]): TreeLayout {
  return {
    getChildrenIds: (id: string) => layout[id].childrenIds,
  };
}
//=============================================================================
//=============================================================================

// TODO: all these functions can be grouped to a 'info' object on hierarchy

// ChildParentMapping: Map from childId to [parentId, index-in-siblings-list]
type ChildParentMapping = Record<string, [string, number]>;

const buildChildParentMapping = (layout: OrgChartElement["layout"]) => {
  const mapping: ChildParentMapping = {};
  for (const key in layout) {
    if (layout[key].childrenIds) {
      layout[key].childrenIds.forEach((childId, index) => (mapping[childId] = [key, index]));
    }
  }
  return mapping;
};

const ancestors = (mapping: ChildParentMapping, id: string) => {
  const result = [];
  while (mapping[id]) {
    result.push(mapping[id][0]);
    id = mapping[id][0];
  }
  return result;
};

const disjointSets = <T>(a: T[], b: T[]) => !any((item) => a.includes(item), b);

const computeHeight = (element: OrgChartElement, root = "root") => {
  const height: Record<string, number> = {};
  const getHeight = (id: string) => {
    if (height[id]) return height[id];
    if (element.layout[id].childrenIds.length == 0) {
      return (height[id] = 0);
    }
    const h: number = Math.max.apply(null, element.layout[id].childrenIds.map(getHeight)) + 1;
    return (height[id] = h);
  };
  getHeight(root);
  return height;
};

export const updatePhotoInNode = produceWithPatches((draft, nodeId, url) => {
  draft.data[nodeId][PHOTO_ID] = url;
});

export const updateFieldInNode = produceWithPatches((draft, nodeId, fieldId, value) => {
  draft.data[nodeId][fieldId] = value;
});

export const toggleCollapseNode = produceWithPatches((draft, nodeId) => {
  draft.layout[nodeId].collapsed = !draft.layout[nodeId].collapsed;
});

const removeNodeFromParent = (draft: Pick<OrgChartElement, "layout">, nodeId: string) => {
  for (const key in draft.layout) {
    const i = draft.layout[key].childrenIds?.indexOf(nodeId);
    if (i != undefined && i != -1) {
      draft.layout[key].childrenIds!.splice(i, 1);
      break;
    }
  }
};

//#region Adding new nodes

const addNewNode = (draft: MinimalTree, nodeId?: string) => {
  nodeId ??= createNodeId();
  draft.layout[nodeId] = { id: nodeId, collapsed: false, childrenIds: [] };
  draft.data[nodeId] = { id: nodeId };
  return nodeId;
};

export const findParent = (element: OrgChartElement, id: string) =>
  Object.keys(element.layout).find((key) => element.layout[key].childrenIds.includes(id));

const findParentAndSiblingNumber = (draft: MinimalTree, nodeId: string): [string | null, number] => {
  for (const key in draft.layout) {
    const i = draft.layout[key].childrenIds?.indexOf(nodeId);
    if (i != undefined && i != -1) {
      return [key, i];
    }
  }
  return [null, -1];
};

const attachNodeUnder = (draft: MinimalTree, nodeId: string, parentId: string, childIndex?: number) => {
  const parent = draft.layout[parentId];
  if (!parent) return;
  parent.childrenIds ??= [];
  if (childIndex == undefined) {
    parent.childrenIds.push(nodeId);
  } else {
    parent.childrenIds.splice(childIndex + 1, 0, nodeId);
  }
};

// add node to orgchart
export const createAndInsertNode = (draft: MinimalTree, referenceNodeId: string, relation: Relationship) => {
  const nodeId = createNodeId();
  let index: number | undefined;
  let insertPoint: string | null = null;
  switch (relation) {
    case "child": {
      insertPoint = referenceNodeId;
      break;
    }
    case "sibling-after": {
      [insertPoint, index] = findParentAndSiblingNumber(draft, referenceNodeId);
      break;
    }
    case "sibling-before": {
      [insertPoint, index] = findParentAndSiblingNumber(draft, referenceNodeId);
      index--;
      break;
    }
    case "parent": {
      throw new Error('Relation "parent" not implemented');
    }
  }
  attachNodeUnder(draft, nodeId, insertPoint!, index);
  draft.layout[nodeId] = { id: nodeId, collapsed: false, childrenIds: [] };
  draft.data[nodeId] = {
    id: nodeId,
    [DRAW_PROPS_ID]: draft.data[referenceNodeId][DRAW_PROPS_ID],
  };
};

//#endregion

export const deleteChildFromOrgChart = produceWithPatches((draft: OrgChartElement, childId) => {
  delete draft.data[childId];
  delete draft.layout[childId];
  removeNodeFromParent(draft, childId);
});

export const deleteNodes = produceWithPatches((draft: OrgChartElement, nodeIds: string[] | null) => {
  if (!nodeIds) return;
  const reverseMap = buildChildParentMapping(draft.layout);
  for (const nodeId of nodeIds) {
    delete draft.data[nodeId];
    delete draft.layout[nodeId];
    const [parent, _] = reverseMap[nodeId];
    if (draft.layout[parent]) {
      draft.layout[parent].childrenIds = draft.layout[parent].childrenIds!.filter((id) => id != nodeId);
    }
  }
});

export const moveNodesToLeftOfAnother = produceWithPatches((draft: OrgChartElement, ids, targetId, targetParentId) => {
  if (!draft.layout[targetParentId].childrenIds) {
    return; // if the parent has no children, we can't move to the left of anything
  }
  for (const nodeId of ids) {
    removeNodeFromParent(draft, nodeId);
    const i = draft.layout[targetParentId].childrenIds!.indexOf(targetId);
    draft.layout[targetParentId].childrenIds!.splice(i, 0, nodeId);
  }
});

export const moveNodesToRightOfAnother = produceWithPatches((draft: OrgChartElement, ids, targetId, targetParentId) => {
  if (!draft.layout[targetParentId].childrenIds) {
    return; // if the parent has no children, we can't move to the right of anything
  }
  for (const nodeId of ids) {
    removeNodeFromParent(draft, nodeId);
    const i = draft.layout[targetParentId].childrenIds!.indexOf(targetId);
    draft.layout[targetParentId].childrenIds!.splice(i + 1, 0, nodeId);
  }
});

export const moveNodesUnderAnother = produceWithPatches((draft: OrgChartElement, ids, targetId) => {
  for (const id of ids) {
    removeNodeFromParent(draft, id);
    attachNodeUnder(draft, id, targetId!);
  }
});

/**
 * Filter the 'ids' list, keep only nodes who don't have ancestors in the list.
 * @param element
 * @param ids
 */
const filterForSubtreeRoots = (element: MinimalTree, ids: string[]) => {
  const revMapping = buildChildParentMapping(element.layout);
  return ids.filter((id) => disjointSets(ancestors(revMapping, id), ids));
};

export const copyNodes = (element: MinimalTree, roots: string[]) => {
  roots = filterForSubtreeRoots(element, roots);

  const allInvolved = new Set<string>();
  function addRec(id: string) {
    if (allInvolved.has(id)) return;
    allInvolved.add(id);
    element.layout[id].childrenIds.forEach(addRec);
  }
  roots.forEach(addRec);

  const topick = [...allInvolved];

  return {
    data: pick(topick, element.data),
    layout: pick(topick, element.layout),
    roots,
  };
};

export const copyPasteInPlace = produceWithPatches(
  (draft: OrgChartElement, nodeIds: string[], copySubtrees = false) => {
    const mapping = buildChildParentMapping(draft.layout);
    // when copying several nodes, there's the option node X and its ancestor Y
    // are both in the list. In that case X is copied twice (if I simply loop on nodeIds)
    // my solution: keep a cache of nodes that were copied, to avoid duplicates.
    // Also, loop on nodeIds by order of height (closest to root first) so we
    // go over the ancestors first and then the leaves.
    // of course, only necessary if we're recursing into subtrees.
    const visited = new Set<string>();

    // bug: endless recursion when copying tree into itself
    function copyNode(rootId: string, parent: string, recurse: boolean) {
      if (visited.has(rootId)) return;
      visited.add(rootId);
      const newId = addNewNode(draft);
      attachNodeUnder(draft, newId, parent);
      draft.data[newId] = { ...draft.data[rootId], id: newId };
      if (recurse) {
        draft.layout[rootId].childrenIds.forEach((childId) => copyNode(childId, newId, recurse));
      }
    }
    if (copySubtrees) {
      const height = computeHeight(draft);
      nodeIds.sort((a, b) => height[b] - height[a]);
    }

    for (const id of nodeIds) {
      copyNode(id, mapping[id][0], copySubtrees);
    }
  }
);

export const copyPasteUnderNewParent = produceWithPatches(
  (draft: OrgChartElement, nodeIds: string[], newParent: string, copySubtrees = false) => {
    if (!draft.layout[newParent]) return;

    // when copying several nodes, there's the option node X and its ancestor Y
    // are both in the list. In that case X is copied twice (if I simply loop on nodeIds)
    // my solution: keep a cache of nodes that were copied, to avoid duplicates.
    // Also, loop on nodeIds by order of height (closest to root first) so we
    // go over the ancestors first and then the leaves.
    // of course, only necessary if we're recursing into subtrees.
    const visited = new Set<string>();

    function copyNode(rootId: string, parent: string, recurse: boolean) {
      if (visited.has(rootId)) return;
      visited.add(rootId);
      const newId = addNewNode(draft);
      attachNodeUnder(draft, newId, parent);
      draft.data[newId] = { ...draft.data[rootId], id: newId };
      if (recurse) {
        draft.layout[rootId].childrenIds.forEach((childId) => copyNode(childId, newId, recurse));
      }
    }
    if (copySubtrees) {
      const height = computeHeight(draft);
      nodeIds.sort((a, b) => height[b] - height[a]);
    }
    nodeIds.forEach((id) => copyNode(id, newParent, copySubtrees));
  }
);

//-------------------------------
/**
 * Walk a n-ary tree (as defined by d3 library) in breadth-first order.
 * Can ignore some subtrees according to a predicate.
 * This generator will yield only the nodes that aren't rejected and their children, making the
 * tree look like a new tree with the rejected nodes removed.
 * @param root - the root of the tree
 * @param ignoreIf - return true if the node should be ignored
 */
function* walkTreeBfs<T extends HierarchyNode<any>>(root: T, ignoreIf: (node: T) => boolean): Generator<T> {
  function* actualWalk(n: T): Generator<T> {
    if (n.children?.length && n.children.some(ignoreIf)) {
      n = { ...n, children: reject(ignoreIf, n.children!) };
      if (n.children!.length == 0) delete n.children;
    }
    yield n;
    if (n.children) {
      for (const child of n.children) {
        yield* actualWalk(child);
      }
    }
  }
  // we check the root separately, since all other nodes in the children list of some other node,
  // and they will be checked there.
  if (ignoreIf(root)) return;
  yield* actualWalk(root);
}

export function calculateDragDropTargets(
  tree: HierarchyPointNode<any>,
  orientation: OrgChartOrientation,
  width: number,
  height: number
) {
  const isHorizontal = LayoutProps[orientation].isHorizontal;
  const w = isHorizontal ? height : width;
  const h = isHorizontal ? width : height;
  const c = LayoutProps[orientation].convert;

  const connections: Array<{
    x: number;
    y: number;
    dir: Relationship;
    node: any;
  }> = [];
  const addDropTarget = (x: number, y: number, node: any, dir: Relationship) => {
    [x, y] = c(x, y);
    connections.push({
      x,
      y,
      dir,
      node,
    });
  };

  //TODO: don't have to use walkTreeBfs, can just use tree.descendants()
  for (const node of walkTreeBfs(tree, () => false)) {
    addDropTarget(node.x, node.y + h * 0.55, node, "child");
    if (node.parent) {
      addDropTarget(node.x + 0.55 * w, node.y, node, "sibling-after");
      addDropTarget(node.x - 0.55 * w, node.y, node, "sibling-before");
    }
  }
  return connections;
}

export function collectDescendants(layout: LayoutData, rootId: string, includeRoot = false): string[] {
  const desc = layout[rootId].childrenIds.flatMap((childId) => collectDescendants(layout, childId, true));
  return includeRoot ? desc.concat([rootId]) : desc;
}

//-------------------------------
export function countOrgChartNodesInReflect(syncService: SyncService<RW> | null | undefined) {
  return syncService?.getReplicache()?.query(async function (tx: ReadTransaction) {
    const allOrgChartsInCanvas = tx.scan({ prefix: canvasElementPrefix + consts.CANVAS_ELEMENTS.ORG_CHART });
    let total = 0;
    for await (const element of allOrgChartsInCanvas) {
      const orgChart = element as OrgChartElement;
      if (orgChart.hidden) continue;
      total += numberOfNodes(orgChart);
    }
    return total;
  });
}
//-------------------------------
export function numberOfNodes(orgChart: OrgChartElement) {
  return Object.keys(orgChart.layout).length;
}
//-------------------------------
export function numberOfVisibleNodes(orgChart: OrgChartElement): number {
  const layout = orgChart.layout;
  const rootId = "root";

  // Count nodes that would be visible (not collapsed or descendants of collapsed nodes)
  function countVisibleNodesRecursive(nodeId: string, visitedNodes: Set<string>): number {
    if (visitedNodes.has(nodeId) || !layout[nodeId]) {
      return 0;
    }

    visitedNodes.add(nodeId);
    let count = 1; // Count the current node

    const node = layout[nodeId];

    // If this node is collapsed, don't count its children
    if (node.collapsed) {
      return count;
    }

    // Otherwise, count all visible children
    for (const childId of node.childrenIds || []) {
      count += countVisibleNodesRecursive(childId, visitedNodes);
    }

    return count;
  }

  return countVisibleNodesRecursive(rootId, new Set());
}
//-------------------------------
export function getAncestors(nodes: NodeData[], ids: string[]) {
  const ancestors: Record<string, boolean> = {};
  const nodesById = indexBy("id", nodes);
  for (const id of ids) {
    let cur: string | undefined = id;
    while (cur && !ancestors[cur]) {
      ancestors[cur] = true;
      cur = nodesById[cur]?.parentId;
    }
  }
  return ancestors;
}
//-------------------------------
/**
 * Find all nodes with embedded photo-urls, and create metadata entries for them.
 * Replace the embedded photo url with the metadata entry id (changes the orgChart in place)
 * @return: array of metadata entries
 */
export function upgradeOrgChartImageUrls(orgChart: OrgChartElement) {
  const newMetadataEntries = [];
  const existingMetadataEntries = [];
  for (const key in orgChart.data) {
    const photo = orgChart.data[key][PHOTO_ID];
    if (!photo) {
      continue;
    }
    if (photo.includes("/")) {
      const entry = buildMetadataEntry("orgchart-photo-" + key, "image/" + fileExt(photo), photo, null);
      orgChart.data[key][PHOTO_ID] = entry.id;
      newMetadataEntries.push([FileMetadataType + "-" + entry.id, entry]);
    } else {
      existingMetadataEntries.push(photo);
    }
  }
  return { newMetadataEntries, existingMetadataEntries };
}

export function isDepartmentAsSource(sourceType: SourceNodeType, shouldEnableDepartmentAsRoot: boolean) {
  return sourceType === SourceNodeType.Department && shouldEnableDepartmentAsRoot;
}

export function generateDepartmentId(columnId: string, departmentIndex: string) {
  return `Department-${columnId}-${departmentIndex}`;
}

export function parseDepartmentFromRootId(rootId: string) {
  const departmentInfo = rootId.split("-");
  return { columnId: departmentInfo[1], departmentId: departmentInfo[2] };
}

export function isDepartmentNode(nodeId: string) {
  const departmentNodeRegex = /^Department-\w+-\w+$/;
  return departmentNodeRegex.test(nodeId);
}

export function isMissingDataForChart(
  items: OrgChartItem[],
  rootNodeId: string,
  shouldEnableDepartmentAsRoot: boolean
) {
  if (!shouldEnableDepartmentAsRoot) {
    return !items || items.length == 0 || !items.find((item) => item.id === rootNodeId);
  }

  if (!items || items.length === 0) return true;

  const isRootInChart = isRootItemInChart(items, rootNodeId);
  const isDepartmentAsSource = isDepartmentNode(rootNodeId);

  return !isDepartmentAsSource && !isRootInChart;
}

export function extractLabelsFromColumn(column: LiveIntegrationColumn) {
  const settings = column.settings;
  const labels = settings?.labels;
  return labels;
}

export function extractLabelsFromDepartment(columns: LiveIntegrationColumn[], departmentColumnId: string) {
  const departmentColumn = columns.find((c: LiveIntegrationColumn) => c.id === departmentColumnId);
  if (!departmentColumn) return {};

  const labels = extractLabelsFromColumn(departmentColumn);
  return labels;
}

export function isRootItemInChart<T extends { id: string }>(items: T[], rootId: string) {
  return !!items.find((item) => item.id === rootId);
}

export function getRootNodeData(nodeId: string, layout: any, shouldEnableDepartmentAsRoot: boolean): RootData {
  const isDepartmentAsSource = shouldEnableDepartmentAsRoot && isDepartmentNode(nodeId);

  const rootId = isDepartmentAsSource ? parseDepartmentFromRootId(nodeId).departmentId : nodeId;
  const rootType = isDepartmentAsSource ? SourceNodeType.Department : SourceNodeType.Person;

  return {
    rootLayout: layout[rootId],
    rootId,
    rootType,
  };
}

/*
  NOTE:
  When the chart's source is a department, the root node has a fixed height, which cancels the coordination between the node itself
  and the collapse/expand button and the connecting lines.
  In order to solve this problem, we need to adjust the root node position on the Y axis
  (only when the orientation is top-to-bottom because that's the height axis).
*/
export function getRootNodeVerticalCorrection(
  rootType: SourceNodeType,
  orientation: OrgChartOrientation,
  rootNodeHeight: number,
  selectedFieldsLength: number = 0,
  shouldEnableDepartmentAsRoot: boolean
): number {
  const isDepartmentSource = isDepartmentAsSource(rootType, shouldEnableDepartmentAsRoot);
  if (!isDepartmentSource || orientation === OrgChartOrientation.LeftToRight) {
    return 0;
  }

  const defaultNodeHeight = NODE_DEFAULT_FIELDS_COUNT * TEXT_FIELD_HEIGHT + NODE_ADDITIONAL_HEIGHT;
  const heightDiff = rootNodeHeight - defaultNodeHeight;

  const translateY = (TEXT_FIELD_HEIGHT / 2) * selectedFieldsLength - heightDiff / 2;
  return translateY;
}

export function isSelectedChartDataFullyLoaded(
  chartsFullDataLoaded: ChartsFullDataLoadedState,
  selectedIds?: string[]
) {
  if (!selectedIds) return true;
  return selectedIds.some((id) => chartsFullDataLoaded[id]);
}

export function useOrgChartPremiumFeatures() {
  const [{ user: boardUser }] = useBoardValue();
  const maxOrgChartNodes = usePlanFeatureValue(PlanFeatureName.ORGCHART_MAX_NODES);
  const canAddOrgChartFields = usePlanFeatureValue(PlanFeatureName.ORGCHART_ADD_NEW_FIELDS);

  if (boardUser?.isAnonymous) {
    return {
      canAddOrgChartFields: true,
      maxOrgChartNodes: PLAN_FEATURES_DEFAULT_VALUES[PlanFeatureName.ORGCHART_MAX_NODES],
    };
  }

  return {
    canAddOrgChartFields,
    maxOrgChartNodes,
  };
}

/**
 * Calculates the estimated bounding box dimensions for an org chart element based on its layout structure.
 * This is used for template measurement when width/height are not available.
 *
 * @param layout - The org chart layout structure
 * @param data  - The org chart data (for node count)
 * @returns An object with width and height, or null if calculation fails
 */
export function calculateOrgChartDimensions(
  layout: OrgChartElement["layout"],
  data: OrgChartElement["data"]
): { width: number; height: number } {
  const FALLBACK_DIMENSIONS = { width: 800, height: 600 };

  try {
    const nodeCount = Object.keys(data).length;

    if (nodeCount === 0 || Object.keys(layout).length === 0) {
      return FALLBACK_DIMENSIONS;
    }

    const calculateDepth = (nodeId: string, visited = new Set<string>()): number => {
      if (visited.has(nodeId)) return 0;
      visited.add(nodeId);
      const node = layout[nodeId];
      if (!node || !node.childrenIds || node.childrenIds.length === 0 || node.collapsed) {
        return 1;
      }
      const maxChildDepth = Math.max(...node.childrenIds.map((childId: string) => calculateDepth(childId, visited)));
      return 1 + maxChildDepth;
    };

    const calculateMaxWidthAtLevel = (nodeId: string, level: number, visited = new Set<string>()): number => {
      if (visited.has(nodeId)) return 0;
      visited.add(nodeId);
      const node = layout[nodeId];
      if (!node) return 0;

      if (level === 0) {
        // Count direct children
        return node.childrenIds && !node.collapsed ? node.childrenIds.length : 1;
      }

      if (!node.childrenIds || node.childrenIds.length === 0 || node.collapsed) {
        return 0;
      }

      return node.childrenIds.reduce((sum: number, childId: string) => {
        return sum + calculateMaxWidthAtLevel(childId, level - 1, visited);
      }, 0);
    };

    const rootId = layout["root"] ? "root" : Object.keys(layout)[0];
    const depth = calculateDepth(rootId);
    const maxWidth = Math.max(...Array.from({ length: depth }, (_, i) => calculateMaxWidthAtLevel(rootId, i)));

    const sizes = Sizes.get().orgChart;
    const pictureRadius = sizes.thumbnailRadius;

    let totalFields = 0;
    let hasPicture = false;
    const sampleNodes = Object.values(data).slice(0, Math.min(5, Object.keys(data).length)); // Sample up to 5 nodes

    for (const nodeData of sampleNodes) {
      if (!nodeData || typeof nodeData !== "object") continue;

      if (nodeData[PHOTO_ID]) {
        hasPicture = true;
      }

      const fieldCount = Object.keys(nodeData).filter(
        (key) => key !== PHOTO_ID && nodeData[key] && String(nodeData[key]).trim().length > 0
      ).length;
      totalFields += fieldCount;
    }

    const avgVisibleFields = sampleNodes.length > 0 ? Math.ceil(totalFields / sampleNodes.length) : 3; // Default to 3 if no data
    const nodeHeight = (avgVisibleFields + 3) * TextFieldHeight + 5 + 5 + (hasPicture ? pictureRadius : 0);

    const nodeWidth = sizes.nodeWidth;
    const horizontalSpacing = nodeWidth * SeparationSiblings;
    const verticalSpacing = SeparationLayers + nodeHeight;

    const width = Math.max(maxWidth * horizontalSpacing, nodeWidth * 2);
    const height = depth * verticalSpacing;

    return { width, height };
  } catch (error) {
    console.error("Error calculating org chart dimensions:", error);
    return FALLBACK_DIMENSIONS;
  }
}
const DefaultNodeStyle: NodeDrawProps = { lineWidth: 1, dash: 0, color: consts.ORG_CHART_COLOR_PALETTE[5] };

const getNodeDrawProps = (element: any, nodeId: string, sizes: SizesConfig["orgChart"]) =>
  Object.assign({}, DefaultNodeStyle, { lineWidth: sizes.lineWidth }, element?.data?.[nodeId]?.[DRAW_PROPS_ID]);

export function orgChartTraits(element: OrgChartElement, nodeId: string): ITraits {
  const id = parseNodeId(nodeId);
  if (!id) return {};
  const sizes = Sizes.get().orgChart;
  const draw = getNodeDrawProps(element, id.nodeId, sizes);
  const color = draw.color;
  const itemView = element.viewType ?? OrgChartViewType.Default;
  const imageField = {
    id: PHOTO_ID,
    title: "Photo",
    type: "image",
    selected: itemView == OrgChartViewType.Default,
  };
  return {
    orgchartOrientation: element.orientation,

    // I'm using mindmap field picker, which expects 'selected' instead of 'hidden' :-(
    orgChartFields: [imageField].concat(
      Object.values(element.extraFields).map((field, index) => ({
        id: field.id,
        title: field.name || `Field ${index + 1}`,
        type: field.type,
        selected: !field.hidden,
      }))
    ),

    orgChartNodeColor: color,
    orgChartNodeStroke: draw,
    orgChartItemViewType: itemView,
  };
}

export function orgChartValidateTraits(element: OrgChartElement, trait: Trait, newValue: any) {
  if (trait == "orgChartFields") {
    // new value is an array of ids of the visible fields
    // we also have photo field there, which is held separately in the element.
    let { viewType = OrgChartViewType.Default, extraFields } = element;

    const withPhoto = newValue.indexOf(PHOTO_ID) != -1;
    if (withPhoto != (viewType == OrgChartViewType.Default)) {
      viewType = withPhoto ? OrgChartViewType.Default : OrgChartViewType.NoProfilePicture;
    }

    // the rest, we'll convert it to the format of element.fields, where 'hidden' reflects visibility
    // create a copy of element.extraField, reset all fields to hidden, and then
    // turn on just those given to us in newValue
    extraFields = structuredClone(extraFields);
    for (const key in extraFields) {
      extraFields[key].hidden = true;
    }
    for (const key of newValue) {
      if (key in extraFields) extraFields[key].hidden = false;
    }
    return { extraFields, viewType };
  } else if (trait == "orgChartNodeColor") {
    const { color } = parseColor(newValue); // this is to get just color value without opacity
    return color;
  } else {
    return newValue;
  }
}

export function orgChartNodeTraits(element: any, specificId: string): ITraits {
  const nodeId = parseNodeId(specificId)!.nodeId;
  const sizes = Sizes.get().orgChart;
  const draw = getNodeDrawProps(element, nodeId, sizes);
  const color = draw.color;

  return {
    orgChartNodeColor: color,
    orgChartNodeStroke: draw,
  };
}

export function orgChartNodeValidateTraits(_element: any, trait: Trait, newValue: any) {
  if (trait == "orgChartNodeColor") {
    const { color } = parseColor(newValue); // this is to get just color value without opacity
    return color;
  }
  return newValue;
}
